import React, { useRef } from "react";
import {
  WSButton,
  WSControl,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInputPassword,
  WSInputText,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import { validatorEmail } from "../../../../shared/validators/validatorEmail";
import { validatorPassword } from "../../../../shared/validators/validatorPassword";
import { InvitationAuthPrivacyPolicyInfoBlock } from "../InvitationAuthPrivacyPolicyInfoBlock";
import { useMutationSubmitSignUpForm } from "../../mutations/useMutationSubmitSignUpForm";
import { GoogleSignIn } from "../GoogleSignIn";
import { useHistory } from "react-router-dom";
import { useWSAnalytics } from "../../../../utils/WSAnalytics";
import { useSetWSStore } from "../../../../store";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { IInvitationResponse } from "@wingspanhq/users";
import { useNotYourEmailPayer } from "../../modals/useNotYourEmailPayer";
import { PlatformContextType } from "@wingspanhq/users/dist/lib/interfaces/newUser";
import { useNotYourEmailPayee } from "../../modals/useNotYourEmailPayee";
import { INVITATION_AUTH_SUCCESS_PATH } from "../../index";
import { IInvitation } from "@wingspanhq/users/dist/lib/interfaces";
import { useMutationIdentifyAuthentication } from "../../../../query/users/mutations";
import {
  buildV3SignInUrl,
  shouldRedirectToV3
} from "../../../../Auth/utils/v3LoginRedirect";

type Props = {
  organizationName: string;
  invitationData: IInvitationResponse;
  invitation: IInvitation;
};

export const InvitationAuthSignUpForm: React.FC<Props> = ({
  organizationName,
  invitationData,
  invitation
}) => {
  const [disabledEmail, setDisabledEmail] = React.useState(
    !!invitationData.invitee.email
  );
  const [submit, submitMeta] = useMutationSubmitSignUpForm(
    invitationData,
    invitation
  );
  const [identifyAuth] = useMutationIdentifyAuthentication();
  const googleMigrateToV3 = useRef(false);
  const notYourEmailPayerModal = useNotYourEmailPayer();
  const notYourEmailPayeeModal = useNotYourEmailPayee();
  const history = useHistory();
  const analytics = useWSAnalytics();
  const setStore = useSetWSStore();
  const inviterName = invitationData?.inviter?.name ?? organizationName;

  /** Sends a new account whose email belongs on v3 to v3 sign-in; everyone else continues in v2. */
  const goToApp = (email: string, migrateToV3: boolean) => {
    const v3SignInUrl = migrateToV3 ? buildV3SignInUrl(email) : null;
    if (v3SignInUrl) {
      // Replace this page in history: Back from v3 sign-in would otherwise reload it in the same tab,
      // where the stored sign-up session opens the signed-in v2 app.
      window.location.replace(v3SignInUrl);
      return;
    }

    history.replace(INVITATION_AUTH_SUCCESS_PATH);
  };

  return (
    <>
      <WSList gap="XL" p="XL">
        <WSText.Paragraph weight="medium">Sign Up</WSText.Paragraph>

        <WSForm
          onSubmit={async values => {
            await submit(values, {
              onSuccess({ migrateToV3 }) {
                setStore({ signUpMethod: "Email" });
                analytics.track.signUp();
                goToApp(values.email, migrateToV3);
              }
            });
          }}
          defaultValues={{
            email: invitationData.invitee.email || "",
            password: "",
            remember: true,
            marketingConsent: false
          }}
          validationSchema={Yup.object().shape({
            email: validatorEmail.required("Email is required"),
            password: validatorPassword.required("Password is required"),
            remember: Yup.boolean()
          })}
        >
          <WSForm.Field
            mb="L"
            name="email"
            label="Email"
            description={`This email was provided by ${inviterName} and will be used to create your account.`}
            component={WSInputText}
            componentProps={{
              disabled: disabledEmail,
              required: true
            }}
          />
          <WSForm.Field
            mb="L"
            name="password"
            label="Create password"
            component={WSInputPassword}
            componentProps={{
              required: true
            }}
          />
          <WSForm.Field
            name="remember"
            component={WSControl}
            componentProps={{
              size: "S",
              type: "checkbox",
              label: "Stay signed in"
            }}
          />
          <WSForm.Field
            name="marketingConsent"
            component={WSControl}
            componentProps={{
              size: "S",
              type: "checkbox",
              label: "Send me product updates and tips from Wingspan"
            }}
          />
          <WSText.ParagraphSm color="gray500">
            Optional. You can unsubscribe at any time.
          </WSText.ParagraphSm>

          <InvitationAuthPrivacyPolicyInfoBlock mt="XL" mb="L" />

          {submitMeta.error?.message === "SingleSignOn" &&
          "url" in submitMeta.error &&
          submitMeta.error.url ? (
            <WSText.ParagraphSm color="red400" mb="XL">
              Your domain <b>{submitMeta.error.domain}</b> has SSO enabled.{" "}
              <WSButton.Link
                size="S"
                onClick={() => {
                  if (submitMeta.error && "url" in submitMeta.error) {
                    window.location.href = submitMeta.error.url;
                  }
                }}
                rightIcon="arrow-right"
              >
                Sign in with SSO
              </WSButton.Link>
            </WSText.ParagraphSm>
          ) : submitMeta.error?.message === "Captcha loading error" ? (
            <WSText color="garnet" my="XL">
              Sorry, something went wrong.{" "}
              <WSButton.Link
                type="button"
                size="M"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Reload and try again
              </WSButton.Link>
            </WSText>
          ) : (
            <WSErrorMessage
              my="XL"
              contextKey="Auth"
              forceShowApiErrors
              error={submitMeta.error}
            />
          )}

          <WSForm.SubmitButton fullWidth size="M">
            Get started with Wingspan
          </WSForm.SubmitButton>
        </WSForm>
      </WSList>
      <WSDivider label="or" />

      <WSElement p="XL">
        <GoogleSignIn
          invitation={invitation}
          email={invitationData.invitee.email}
          title="Sign-in with Google"
          onFailure={() => {}}
          onBeforeClick={openGoogle => {
            // Google sign-up has no earlier identify call to reuse. Start one alongside the Google
            // pop-up instead of after sign-up: its reCAPTCHA fetch can wait 30s on a blocked script
            // or never settle, which would freeze the page after the account already exists.
            const checkV3 = async () => {
              try {
                const response = await identifyAuth(
                  { email: invitationData.invitee.email },
                  { throwOnError: true }
                );
                googleMigrateToV3.current =
                  !!response && shouldRedirectToV3(response);
              } catch {
                // A failed routing check keeps the person on v2, as before.
              }
            };
            checkV3();
            openGoogle();
          }}
          onSuccess={() => {
            setStore({ signUpMethod: "Google" });
            analytics.track.signUp();
            // An answer that has not arrived yet keeps the person on v2, as before.
            goToApp(invitationData.invitee.email, googleMigrateToV3.current);
          }}
        />
      </WSElement>

      <WSDivider />

      <WSFlexBox p="XL" justify="space-between">
        {/*<WSElement>
          <WSText.ParagraphXs inline color="gray500" mr="S">
            Already a member?
          </WSText.ParagraphXs>
          <WSButton.Link size="S" rightIcon="chevron-right">
            Sign in
          </WSButton.Link>
        </WSElement>*/}
        <WSButton.Link
          size="S"
          rightIcon="chevron-right"
          onClick={async () => {
            let result: boolean | null = false;
            if (invitationData.context === PlatformContextType.Payer) {
              result = await notYourEmailPayerModal.open({
                email: invitationData.invitee.email
              });
            } else {
              result = await notYourEmailPayeeModal.open({
                email: invitationData.invitee.email,
                userId: invitationData.invitee.userId,
                userName: invitationData.invitee.name,
                payerName: invitationData.inviter?.name
              });
            }

            if (result) {
              setDisabledEmail(false);
            }
          }}
        >
          Not your email?
        </WSButton.Link>
      </WSFlexBox>
    </>
  );
};
